import { AsyncPipe, DecimalPipe, NgClass, UpperCasePipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { HeaderComponent } from "@components/header/header.component";
import { HotDocComponent } from "@components/hot-doc/hot-doc.component";
import { LabelComponent } from "@components/label/label.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { SpanComponent } from "@components/span/span.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import _, { cloneDeep, isNil } from "lodash";
import { BehaviorSubject, Subject } from "rxjs";
import { Observable } from "rxjs/internal/Observable";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { ToastService } from "~app/common/components/toast/toast.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { UpdateSnabbkalkylmallCommand } from "~app/features/virkeskop/commands/tradslag/UpdateSnabbkalkylmallCommand";
import { VKForkalkylTradslagDTO } from "~app/features/virkeskop/models/tradslag/tradslag-models";
import { UtbyteStatusKod } from "~app/features/virkeskop/models/tradslag/utbytestatus";
import { AtgardMediatorService } from "~app/features/virkeskop/services/atgard-mediator.service";
import { AvtalService } from "~app/features/virkeskop/services/avtal.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { TradslagMediatorService } from "~app/features/virkeskop/services/tradslag-mediator.service";
import { DirektutbyteBerakningRequest, VKDirektutbyte, VKDirektutbyteTradslagDTO, allTradslagHasValues } from "../../../models/tradslag/direktutbyte-models";
import { ForkalkylDirektutbyteClientValidationService } from "../../../services/validation/forkalkyl-direktutbyte-client-validation.service";
import { VirkeskopValidationIconPopoverComponent } from "../../../validation/validation-icon-popover/virkeskop-validation-icon-popover.component";
import { AddTradslagButtonComponent } from "../add-tradslag-button/add-tradslag-button.component";
import { ForkalkylArborealComponent } from "../forkalkyl-arboreal/forkalkyl-arboreal.component";
import { TradslagUtbyteComponent } from "../tradslag-utbyte/tradslag-utbyte.component";
import { TradslagColumnDefinition, TradslagTableBinder } from "../utilities/tradslag-table-binder";

export type DirektutbyteColumnDefinition = TradslagColumnDefinition<VKDirektutbyteTradslagDTO>
    & { cellName: keyof Omit<VKDirektutbyteTradslagDTO, "tradslag" | "guid"> };

const grundytevagd = (values: number[], items: VKDirektutbyteTradslagDTO[]) => {
    const summaValuesGrundyta = values.reduce((sum, x, i) => sum + x * items[i].grundyta, 0);
    const summaGrundyta = _.sumBy(items, (x) => x.grundyta);
    return _.round(summaValuesGrundyta / summaGrundyta, 1);
};
const stamantalvagd = (values: number[], items: VKDirektutbyteTradslagDTO[]) => {
    const summaValuesStamantal = values.reduce((sum, x, i) => sum + x * items[i].stammarperHa, 0);
    const summaStamantal = _.sumBy(items, (x) => x.stammarperHa);
    return _.round(summaValuesStamantal / summaStamantal, 2);
};

export const validateAsDecimal = (value: any) => {
    if (_.isNaN(value)) {
        return false;
    }

    const decimal = Number.parseFloat(value);
    return _.isNumber(decimal) && decimal >= 0;
};

export const direktutbyteColumns: DirektutbyteColumnDefinition[] = [
    {
        header: "m3sk/ha",
        cellName: "m3skHa",
        readOnly: false,
        type: "numeric",
        sumFunction: _.sum,
        tooltip: "Volym per skogskubik"
    },
    {
        header: "GY",
        cellName: "grundyta",
        readOnly: false,
        type: "numeric",
        sumFunction: _.sum,
        tooltip: "Grundyta",
        validateFn: validateAsDecimal
    },
    {
        header: "DGV",
        cellName: "diameterGrundytevagd",
        readOnly: false,
        type: "numeric",
        sumFunction: grundytevagd,
        tooltip: "Diameter grundytevägd",
        validateFn: validateAsDecimal
    },
    {
        header: "HGV",
        cellName: "hojdGrundyteVagd",
        readOnly: false,
        type: "numeric",
        sumFunction: grundytevagd,
        tooltip: "Höjd grundytevägd",
        validateFn: validateAsDecimal
    },
    {
        header: "st/ha",
        cellName: "stammarperHa",
        readOnly: false,
        type: "numeric",
        sumFunction: _.sum,
        tooltip: "Stamantal per ha"
    },
    {
        header: "mstam",
        cellName: "medelstam",
        readOnly: false,
        type: "numeric",
        sumFunction: stamantalvagd,
        tooltip: "Medelstam m3f/träd - före avverkning, hela beståndet",
        validateFn: validateAsDecimal
    }
];

@Component({
    selector: "rask-forkalkyl-direktutbyte",
    templateUrl: "./forkalkyl-direktutbyte.component.html",
    styleUrls: ["./forkalkyl-direktutbyte.component.scss"],
    providers: [ForkalkylDirektutbyteClientValidationService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        ForkalkylArborealComponent,
        TextmultilineComponent,
        SpanComponent,
        NgClass,
        HotDocComponent,
        AddTradslagButtonComponent,
        ButtonComponent,
        VirkeskopValidationIconPopoverComponent,
        LabelComponent,
        MessagePanelComponent,
        TradslagUtbyteComponent,
        AsyncPipe,
        UpperCasePipe,
        DecimalPipe
    ]
})
export class ForkalkylDirektutbyteComponent implements OnInit, OnDestroy {
    @Input() readonly = false;

    protected infoMessages: IMessageModel[] = [];
    protected isAnyImported = false;
    protected model: VKDirektutbyte;

    private readonly anyValidationErrors = true;
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly avtalService = inject(AvtalService);
    private readonly binder = new TradslagTableBinder<VKDirektutbyteTradslagDTO>(direktutbyteColumns);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly forkalkylDirektutbyteClientValidationService = inject(ForkalkylDirektutbyteClientValidationService);
    private readonly infoIconProduktgrupper: string[]  = [
        "Talltimmer",
        "Grantimmer"
    ];
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly messagesSrc = new BehaviorSubject<IMessageModel[]>([]);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly ngZone = inject(NgZone);
    private readonly toastService = inject(ToastService);
    private readonly tradslagMediatorService = inject(TradslagMediatorService);

    private hasChanges = false;
    private templateModel: VKDirektutbyte;

    get messages$() {
        return this.messagesSrc.asObservable();
    }

    get selectedTradslag() {
        return this.model?.direktutbyteTradslag
            .map(t => t.tradslag);
    }

    protected get atgard() {
        return this.atgardMediatorService.atgard;
    }

    protected get hotDoc() {
        return this.binder.hotDoc;
    }

    protected get htIdentifier() {
        return `direktutbyteHT/${this.atgard.guid}`;
    }

    protected get totalVolym() {
        const volym = this.summaVolym();
        return _.round(volym, 0);
    }

    protected get utbyteTimmerAndel() {
        return this.model.utbyte?.timmerandel;
    }

    protected get uttagM3f() {
        const uttag = (this.summaVolym() ?? 0) / (this.atgard.nettoareal ?? 0);
        return _.round(uttag, 0);
    }

    ngOnDestroy(): void {
        this.forkalkylDirektutbyteClientValidationService.clearValidations();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.binder.modelChange$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.ngZone.run(() => {
                    this.setIsAnyImported();
                });
                this.handleModelChange();
            });

        this.applyModel(this.tradslagMediatorService.direktutbyte);
        this.validateModel();
        this.setIsAnyImported();

        this.tradslagMediatorService.onDirektutbyteTradslagImported$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe( d => {
                this.applyModel(d);
                this.setIsAnyImported();
                this.validateModel();
                this.tradslagMediatorService.recalculateGrundytaFore();
            });

        this.tradslagMediatorService.onMallSelected$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([
                snabbkalkylmall,
                direktutbytemall
            ]) => {
                this.applyModel(direktutbytemall);
                this.templateModel = cloneDeep(direktutbytemall);
                this.handleModelChange();
            });

        this.tradslagMediatorService.onSaveMall$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((mallNamn: string) => {
                this.busyService.addWork(this.handleSaveMall(mallNamn))
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(result => {
                        if (result) {
                            this.toastService.add(`Direktutbytemallen '${mallNamn}' sparades`);
                            this.tradslagMediatorService.onMallChanged.next();
                        }
                    });
            });

        this.kontraktMediatorService.kontrakt$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((payload) => {
                if (payload !== null) {
                    const cleared = isNil(this.templateModel);
                    if (!cleared) {
                        this.templateModel = null;
                        this.handleModelChange();
                    }
                }
            });
    }

    protected calculateDirektutbyte(): void {
        if (this.readonly) {
            return;
        }
        const request: DirektutbyteBerakningRequest = {
            tradslag: this.model.direktutbyteTradslag,
            atgardguid: this.atgard.guid,
            atgardbeteckning: this.atgard.beteckning,
            geometri: this.atgard.geometri,
            nettoareal: this.atgard.nettoareal,
            atgardAvverkningsTyp: this.atgard.atgardstyp
        };

        this.busyService.addWork(this.avtalService.calculateDirekUtbyte(request))
            .subscribe({
                next: beraknatUtbyte => {
                    // Mappar mot de propertys som finns.
                    _.assign(this.model, beraknatUtbyte);

                    this.handleModelChange(true);
                    this.hasChanges = false;
                },
                error: () => {
                //
                }
            });
    }

    protected getBeraknadTid(): string {
        return DateUtils.formatDateString(this.model?.beraknadTid, DateUtils.datetimeformatWithoutMillis);
    }

    protected getProduktgruppVolym = (volym: number) => {
        const summa = volym * (this.atgard.nettoareal ?? 0);
        return _.round(summa, 0);
    };

    protected onTradslagAdded(tradslag: VKForkalkylTradslagDTO) {
        this.model.direktutbyteTradslag.push({
            tradslag,
            m3skHa: 0,
            grundyta: 0,
            diameterGrundytevagd: 0,
            hojdGrundyteVagd: 0,
            stammarperHa: 0,
            medelstam: 0
        });

        this.handleModelChange();
        this.binder.rebind();
    }

    protected showCalculate(): boolean {
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if((_.isNil(this.model.beraknadTid) || (!_.isNil(this.model.beraknadTid) && this.hasChanges))
            && this.model.direktutbyteTradslag?.length > 0
            && allTradslagHasValues(this.model?.direktutbyteTradslag)) {
            return true;
        }
        return false;
    }

    protected showInfoIcon(namn: string): boolean {
        return this.infoIconProduktgrupper.includes(namn);
    }

    private addMessage(message: IMessageModel): void {
        this.infoMessages.push(message);
    }

    private applyModel(model: VKDirektutbyte) {
        this.model = model;
        this.binder.initTable(this.model.direktutbyteTradslag);
    }

    private clearMessages(): void {
        this.infoMessages.splice(0);
        this.messagesSrc.next([]);
    }

    private handleErrorMessages(model: VKDirektutbyte ) {
        this.clearMessages();
        if(!_.isNil(model.direktutbyteText) && model.direktutbyteStatus !== UtbyteStatusKod.OK) {
            const message = {
                severity: MessageSeverity.Info,
                message: `${model.direktutbyteStatus  }: ${  model.direktutbyteText}`
            } as IMessageModel;

            this.addMessage(message);
        }

        this.showMessages();
    }

    private handleModelChange(isCalculate = false) {
        this.validateModel();

        this.hasChanges = true;
        this.tradslagMediatorService.recalculateMedelstam();
        this.tradslagMediatorService.recalculateGrundytaFore();

        this.handleErrorMessages(this.model);

        this.model.importKalla = !this.isAnyImported ? null : this.model.importKalla;
        this.tradslagMediatorService.updateDirektutbyte(this.atgard.guid, this.model, isCalculate);
    }

    private handleSaveMall(mallNamn: string): Observable<any> {
        const snabbkalkylmall = this.model.toMallDto(mallNamn);

        const command = new UpdateSnabbkalkylmallCommand(this.atgard.guid, snabbkalkylmall);

        this.commandService.addTransactionCommand(command);
        const obs = this.commandService.pushCommandQueueTransaction();
        obs.pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(x => {
                this.commandService.reset();
            });

        return obs;
    }

    private setIsAnyImported(): void {
        const importedTradslagsdata = this.tradslagMediatorService.importedUtbyteTradslag;
        let anySameAsImport = false;

        if(importedTradslagsdata) {
            this.model.direktutbyteTradslag.forEach(current => {
                const importedTradslag = importedTradslagsdata.find( i => i.tradslag.id === current.tradslag.id);
                if(importedTradslag) {
                    const propertiesToCheck: (keyof VKDirektutbyteTradslagDTO)[] = [
                        "diameterGrundytevagd",
                        "grundyta",
                        "hojdGrundyteVagd",
                        "m3skHa",
                        "medelstam",
                        "stammarperHa"
                    ];

                    propertiesToCheck.forEach(property => {
                        if (importedTradslag[property] === current[property] && !_.isNil(importedTradslag[property])) {
                            anySameAsImport = true;
                        }
                    });
                }
            });

            if (!anySameAsImport) {
                this.tradslagMediatorService.removeImportedDirektutbyteInMemory();
            }

            this.isAnyImported = anySameAsImport;
        } else {
            this.isAnyImported = !_.isNil(this.model?.importKalla);
        }
    }

    private showMessages(): void {
        if(this.infoMessages.length > 0) {
            this.messagesSrc.next(this.infoMessages );
        }
    }

    private summaVolym(): number {
        let total = 0;

        if(!_.isNil(this.model?.utbyte)) {
            for(const row of this.model.utbyte.produktgrupper) {
                total += this.getProduktgruppVolym(row.kvantitetHa);
            }
        }

        return total;
    }

    private validateModel() {
        const [cellStyles] = this.forkalkylDirektutbyteClientValidationService.updateValidationTableFields(this.templateModel);
        this.binder.setCellStyles(cellStyles);
    }
}
